/**
 * Portal Payment Setup
 * Add/manage payment methods via Stripe Elements
 * Supports Apple Pay, Google Pay, Cards, and more
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import {
  validatePortalAccess,
  createPortalSetupIntent,
  attachPortalPaymentMethod,
  getPortalPaymentMethods,
  setDefaultPaymentMethod,
  removePaymentMethod,
  getPortalInvoices,
} from '../services/portalService';
import { getActivePublishableKey } from '../services/stripeConfigService';
import { formatCurrency } from '../utils/formatters';
import { ContractEquivalent } from '../components/ContractEquivalent';
import './PortalPaymentSetup.css';

// Payment Element appearance
const appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#3b82f6',
    colorBackground: '#ffffff',
    colorText: '#1e293b',
    colorDanger: '#ef4444',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '2px solid #e2e8f0',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '2px solid #3b82f6',
      boxShadow: 'none',
    },
    '.Label': {
      fontWeight: '500',
      color: '#475569',
    },
  },
};

// Payment Form Component (uses Stripe hooks)
const PaymentForm = ({ accessToken, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      // Confirm the setup
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/portal/${accessToken}/payment-setup?success=true`,
        },
        redirect: 'if_required',
      });

      if (error) {
        throw new Error(error.message);
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Attach payment method to customer on backend
        await attachPortalPaymentMethod(accessToken, setupIntent.payment_method);
        onSuccess();
      } else if (setupIntent && setupIntent.status === 'processing') {
        // Some payment methods (like bank accounts) may take time
        onSuccess('Payment method is being processed. It may take a few days to verify.');
      } else {
        throw new Error('Setup was not successful. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      {errorMessage && (
        <div className="payment-error-message">{errorMessage}</div>
      )}

      <PaymentElement
        options={{
          layout: 'tabs',
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
        }}
      />

      <div className="secure-notice">
        <span className="lock-icon">🔒</span>
        <span>Your payment information is secured with bank-level encryption</span>
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || processing}
        >
          {processing ? 'Adding...' : 'Add Payment Method'}
        </button>
      </div>
    </form>
  );
};

const PortalPaymentSetup = () => {
  const { accessToken } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  // Pending invoices surfaced after a card is added so the user can pay
  // immediately instead of hunting through tabs. Populated lazily on
  // successful add (not on initial page load) so we only call the API
  // when it's relevant.
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [stripePromise, setStripePromise] = useState(null);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const [accessResult, methodsResult, pubKey] = await Promise.all([
          validatePortalAccess(accessToken),
          getPortalPaymentMethods(accessToken),
          getActivePublishableKey(),
        ]);

        if (!accessResult.success) {
          throw new Error('Invalid access');
        }

        if (!accessResult.permissions.canUpdatePayment) {
          throw new Error('Not authorized to manage payment methods');
        }

        setClient(accessResult.client);
        setPaymentMethods(methodsResult.paymentMethods || []);
        setStripePromise(loadStripe(pubKey));
        setIsTestMode(pubKey?.startsWith('pk_test_'));

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [accessToken]);

  const handleAddPayment = async () => {
    setSetupLoading(true);
    setSuccessMessage(null);
    try {
      // Get setup intent from server
      const intentResult = await createPortalSetupIntent(accessToken);
      if (!intentResult.success || !intentResult.clientSecret) {
        throw new Error('Failed to initialize payment setup');
      }
      setClientSecret(intentResult.clientSecret);
      // Use the key the backend used — guarantees publishable key matches the intent mode
      if (intentResult.publicKey) {
        setStripePromise(loadStripe(intentResult.publicKey));
        setIsTestMode(intentResult.publicKey?.startsWith('pk_test_'));
      }
      setShowAddPayment(true);
    } catch (err) {
      alert('Failed to start payment setup: ' + err.message);
    } finally {
      setSetupLoading(false);
    }
  };

  const handlePaymentAdded = async (message) => {
    setShowAddPayment(false);
    setClientSecret(null);
    if (message) {
      setSuccessMessage(message);
    } else {
      setSuccessMessage('Payment method added successfully!');
    }
    // Reload payment methods AND surface anything outstanding so the user
    // doesn't have to navigate elsewhere to actually pay.
    const [methodsResult, invoicesResult] = await Promise.all([
      getPortalPaymentMethods(accessToken),
      getPortalInvoices(accessToken).catch(() => ({ invoices: [] })),
    ]);
    setPaymentMethods(methodsResult.paymentMethods || []);
    const pending = (invoicesResult.invoices || []).filter(
      (inv) => inv.status !== 'paid' && inv.status !== 'void',
    );
    setPendingInvoices(pending);
  };

  const handleCancel = () => {
    setShowAddPayment(false);
    setClientSecret(null);
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await setDefaultPaymentMethod(accessToken, paymentMethodId);
      // Reload payment methods
      const methodsResult = await getPortalPaymentMethods(accessToken);
      setPaymentMethods(methodsResult.paymentMethods || []);
    } catch (err) {
      alert('Failed to set default payment method');
    }
  };

  const handleRemove = async (paymentMethodId) => {
    if (!window.confirm('Remove this payment method?')) return;

    try {
      await removePaymentMethod(accessToken, paymentMethodId);
      // Reload payment methods
      const methodsResult = await getPortalPaymentMethods(accessToken);
      setPaymentMethods(methodsResult.paymentMethods || []);
    } catch (err) {
      alert('Failed to remove payment method');
    }
  };

  if (loading) {
    return (
      <div className="payment-setup-container">
        <div className="payment-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-setup-container">
        <div className="payment-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate(`/portal/${accessToken}`)}>
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-setup-container">
      <header className="payment-setup-header">
        <button className="back-button" onClick={() => navigate(`/portal/${accessToken}`)}>
          &larr; Back to Portal
        </button>
        <h1>Payment Methods</h1>
      </header>

      <main className="payment-setup-main">
        {successMessage && (
          <div className="success-message">
            <span>✓</span> {successMessage}
          </div>
        )}

        {pendingInvoices.length > 0 && (
          <div className="pending-pay-card" role="region" aria-label="Pending invoices">
            <div className="pending-pay-card-header">
              <div>
                <h2>
                  {pendingInvoices.length === 1
                    ? 'You have 1 pending invoice'
                    : `You have ${pendingInvoices.length} pending invoices`}
                </h2>
                <p className="pending-pay-card-sub">
                  Your card is saved. Pay outstanding invoices now or come back later.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-primary pending-pay-card-cta"
                onClick={() => navigate(`/portal/${accessToken}`)}
              >
                Go to Invoices &rarr;
              </button>
            </div>
            <ul className="pending-pay-list">
              {pendingInvoices.slice(0, 5).map((inv) => (
                <li key={inv.id} className="pending-pay-item">
                  <span className="pending-pay-number font-mono">{inv.invoiceNumber}</span>
                  <span className="pending-pay-amount">
                    {formatCurrency(inv.totalAmount, inv.currency)}
                    <ContractEquivalent invoice={inv} className="inline" />
                  </span>
                </li>
              ))}
              {pendingInvoices.length > 5 && (
                <li className="pending-pay-item pending-pay-more">
                  +{pendingInvoices.length - 5} more
                </li>
              )}
            </ul>
          </div>
        )}

        <div className="payment-methods-section">
          <div className="section-header">
            <h2>Your Payment Methods</h2>
            {!showAddPayment && (
              <button
                className="btn btn-primary"
                onClick={handleAddPayment}
                disabled={setupLoading}
              >
                {setupLoading ? 'Loading...' : '+ Add Payment Method'}
              </button>
            )}
          </div>

          {paymentMethods.length === 0 && !showAddPayment ? (
            <div className="empty-state">
              <div className="empty-icon">💳</div>
              <p>No payment methods on file.</p>
              <p className="text-small">Add a card, Apple Pay, Google Pay, or bank account to enable automatic payments.</p>
              <button
                className="btn btn-primary"
                onClick={handleAddPayment}
                disabled={setupLoading}
              >
                {setupLoading ? 'Loading...' : 'Add Your First Payment Method'}
              </button>
            </div>
          ) : (
            <div className="payment-methods-grid">
              {paymentMethods.map(pm => (
                <div key={pm.id} className={`payment-card ${pm.isDefault ? 'default' : ''}`}>
                  <div className="card-visual">
                    <div className="card-brand-logo">{getPaymentMethodIcon(pm)}</div>
                    <div className="card-number-display">{getPaymentMethodDisplay(pm)}</div>
                    {pm.expMonth && pm.expYear && (
                      <div className="card-expiry-display">
                        {pm.expMonth.toString().padStart(2, '0')}/{pm.expYear.toString().slice(-2)}
                      </div>
                    )}
                  </div>
                  <div className="card-actions">
                    {pm.isDefault ? (
                      <span className="default-label">Default</span>
                    ) : (
                      <button className="btn-link" onClick={() => handleSetDefault(pm.id)}>
                        Set as default
                      </button>
                    )}
                    <button className="btn-link danger" onClick={() => handleRemove(pm.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showAddPayment && clientSecret && (
          <div className="add-payment-section">
            <h3>Add Payment Method</h3>
            <div className="payment-options-info">
              <p>Choose from cards, Apple Pay, Google Pay, or bank accounts depending on your region.</p>
            </div>
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance,
              }}
            >
              <PaymentForm
                accessToken={accessToken}
                clientSecret={clientSecret}
                onSuccess={handlePaymentAdded}
                onCancel={handleCancel}
              />
            </Elements>

            <div className="stripe-notice">
              <span style={{ color: '#635BFF', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.5px' }}>stripe</span>
              <p className="text-small">Secure payment processing by Stripe</p>
            </div>
          </div>
        )}

        <div className="supported-methods-section">
          <h3>Supported Payment Methods</h3>
          <div className="payment-icons">
            <div className="payment-icon" title="Visa">
              <span>VISA</span>
            </div>
            <div className="payment-icon" title="Mastercard">
              <span>MC</span>
            </div>
            <div className="payment-icon" title="American Express">
              <span>AMEX</span>
            </div>
            <div className="payment-icon apple-pay" title="Apple Pay">
              <span> Pay</span>
            </div>
            <div className="payment-icon google-pay" title="Google Pay">
              <span>G Pay</span>
            </div>
            <div className="payment-icon" title="Link by Stripe">
              <span>Link</span>
            </div>
          </div>
        </div>

        <div className="info-section">
          <h3>About Automatic Payments</h3>
          <p>
            When you add a payment method and enable auto-pay, your invoices will be
            automatically charged on the due date. You'll receive an email confirmation
            after each successful payment.
          </p>
          <ul>
            <li>No late fees or service interruptions</li>
            <li>Secure payment processing via Stripe</li>
            <li>Cancel auto-pay anytime from this portal</li>
          </ul>
        </div>

        {isTestMode && (
          <div className="test-card-notice">
            <h4>Test Mode Active</h4>
            <p>Use these test card numbers:</p>
            <ul>
              <li><strong>Success:</strong> 4242 4242 4242 4242</li>
              <li><strong>Decline:</strong> 4000 0000 0000 0002</li>
              <li>Any future expiry, any 3-digit CVC, any postal code</li>
            </ul>
          </div>
        )}
      </main>
    </div>
  );
};

function getPaymentMethodIcon(pm) {
  if (pm.wallet === 'apple_pay') return ' Pay';
  if (pm.wallet === 'google_pay') return 'G Pay';
  if (pm.type === 'link') return '🔗 Link';
  if (pm.type === 'us_bank_account') return '🏦 Bank';
  if (pm.type === 'sepa_debit') return '🏦 SEPA';

  const icons = {
    visa: '💳 VISA',
    mastercard: '💳 MC',
    amex: '💳 AMEX',
    discover: '💳 DISC',
  };
  return icons[pm.brand?.toLowerCase()] || '💳 Card';
}

function getPaymentMethodDisplay(pm) {
  if (pm.wallet === 'apple_pay') return `Apple Pay •••• ${pm.last4}`;
  if (pm.wallet === 'google_pay') return `Google Pay •••• ${pm.last4}`;
  if (pm.type === 'link') return pm.email || 'Link Account';
  if (pm.type === 'us_bank_account') return `${pm.bankName || 'Bank'} •••• ${pm.last4}`;
  if (pm.type === 'sepa_debit') return `SEPA •••• ${pm.last4}`;

  return `•••• •••• •••• ${pm.last4}`;
}

export default PortalPaymentSetup;
